import React, { useEffect, useState } from "react";
import "./BirthdayCelebration.css";

const birthdayHighlights = [
  "Your own private birthday space for the whole slot",
  "A ready-to-celebrate pre-decorated theatre, with no setup needed",
  "Birthday cake, snacks, food and beverages",
  "Bouquets, gifts and surprise reveals",
  "A movie or entertainment of your choice on the big screen",
];

const birthdaySteps = [
  {
    number: "01",
    title: "Share Your Plans",
    description:
      "Tell us your occasion, preferred date, time and number of guests.",
  },
  {
    number: "02",
    title: "Choose Your Add-Ons",
    description:
      "Choose the add-ons you want, such as cake, food, gifts or special effects.",
  },
  {
    number: "03",
    title: "Arrive and Celebrate",
    description:
      "Our team confirms your slot, and you walk into a pre-decorated private theatre that is ready to celebrate.",
  },
];

const birthdayFaqs = [
  {
    question: "How many guests can attend a birthday celebration?",
    answer:
      "The guest limit depends on the private theatre you book. Share your group size with our team and they will suggest the right option.",
  },
  {
    question: "Can I bring my own cake?",
    answer:
      "Cakes can be added through HexaPlay. Please confirm the current outside cake and food policy with our team before your booking.",
  },
  {
    question: "Can I customize the birthday decoration?",
    answer:
      "No. HexaPlay private theatres have a fixed, pre-installed decor setup that is ready to celebrate. You can personalize the celebration through add-ons such as cakes, gifts and fog entry.",
  },
];

const popupContent = {
  plan: {
    icon: "🎉",
    title: "Plan Your Birthday",
    description:
      "Make your birthday special in a private HexaPlay theatre. The space comes pre-decorated, so you can focus on celebrating with your friends and family.",
    points: [
      "Share your preferred date and time.",
      "Tell us how many guests will attend.",
      "Choose available food, cake, gift and entertainment add-ons.",
      "Confirm the available theatre slot and package details with the team.",
    ],
    note:
      "Availability, guest capacity and package inclusions should be confirmed with HexaPlay before finalizing your plans.",
  },
  addons: {
    icon: "🎂",
    title: "Explore Birthday Add-Ons",
    description:
      "Add extra touches to make your celebration more personal. Available options may vary by package and booking.",
    points: [
      "Birthday cakes and celebration treats.",
      "Bouquets, gifts and surprise arrangements.",
      "Fog entry and special entrance effects.",
      "Karaoke, movies and entertainment options.",
      "Food and beverages, where available.",
    ],
    note:
      "The theatre decoration is pre-installed and cannot be customized. Please confirm current add-on options and prices with HexaPlay.",
  },
  album: {
    icon: "📸",
    title: "Birthday Celebration Album",
    description:
      "Get inspired by birthday celebrations, cake-cutting moments and surprise experiences at HexaPlay.",
    points: [
      "Birthday celebration inspiration.",
      "Cake-cutting and special moments.",
      "Surprise entries and group celebrations.",
    ],
    note:
      "The gallery on this page currently contains placeholders. Real HexaPlay photos can be added when they are available.",
  },
};

const BirthdayCelebration = () => {
  const [activePopup, setActivePopup] = useState(null);

  const openPopup = (popupName) => {
    setActivePopup(popupContent[popupName]);
  };

  const closePopup = () => {
    setActivePopup(null);
  };

  useEffect(() => {
    if (!activePopup) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setActivePopup(null);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [activePopup]);

  return (
    <main className="birthday-page">
      {/* Hero */}
      <section className="birthday-hero">
        <div className="birthday-hero-glow birthday-glow-one" />
        <div className="birthday-hero-glow birthday-glow-two" />

        <div className="birthday-container birthday-hero-content">
          <span className="birthday-eyebrow">
            YOUR DAY. YOUR PEOPLE. YOUR CELEBRATION.
          </span>

          <h1>Birthday Celebration in a Private Theatre</h1>

          <p className="birthday-hero-description">
            Make your birthday truly special with a private and personalized
            celebration at HexaPlay. Whether you are planning a surprise for
            someone you love or celebrating with friends and family, a private
            theatre gives you the space to cut the cake, watch a movie and enjoy
            the day without a crowd around you.
          </p>

          <p className="birthday-hero-description">
            Your birthday celebration starts the moment you walk in: the theatre
            is pre-decorated and ready, so there is nothing to set up. Add a
            cake, food, gifts and special touches of your choice and spend your
            time on the people who matter.
          </p>

          <div className="birthday-hero-actions">
            <button
              type="button"
              className="birthday-btn birthday-btn-primary"
              onClick={() => openPopup("plan")}
            >
              Plan Your Birthday <span aria-hidden="true">↗</span>
            </button>

            <button
              type="button"
              className="birthday-btn birthday-btn-secondary"
              onClick={() => openPopup("addons")}
            >
              Explore Add-Ons
            </button>
          </div>

          <div className="birthday-hero-tags">
            <span>Private Theatre</span>
            <span>Pre-Decorated Setup</span>
            <span>Personalized Add-Ons</span>
          </div>
        </div>
      </section>

      {/* Birthday Highlights */}
      <section className="birthday-section birthday-highlights-section">
        <div className="birthday-container">
          <div className="birthday-section-heading">
            <span className="birthday-eyebrow">MAKE IT YOUR MOMENT</span>
            <h2>Make Your Birthday Special With</h2>
            <p>
              Everything you need for a memorable birthday, brought together
              in one private celebration space.
            </p>
          </div>

          <div className="birthday-highlights-grid">
            {birthdayHighlights.map((highlight, index) => (
              <article className="birthday-highlight-card" key={highlight}>
                <span className="birthday-highlight-number">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="birthday-highlight-icon" aria-hidden="true">
                  {["✦", "♡", "♧", "◇", "▶"][index]}
                </span>
                <h3>{highlight}</h3>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="birthday-section birthday-process-section">
        <div className="birthday-container">
          <div className="birthday-section-heading">
            <span className="birthday-eyebrow">
              SIMPLE FROM START TO FINISH
            </span>
            <h2>How It Works</h2>
            <p>
              Plan your celebration in a few simple steps and leave the setup
              to us.
            </p>
          </div>

          <div className="birthday-process-grid">
            {birthdaySteps.map((step) => (
              <article className="birthday-process-card" key={step.number}>
                <span className="birthday-process-number">{step.number}</span>
                <div className="birthday-process-line" />
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Add-Ons */}
      <section className="birthday-section birthday-addons-section">
        <div className="birthday-container birthday-addons-layout">
          <div className="birthday-addons-copy">
            <span className="birthday-eyebrow">
              ADD YOUR PERSONAL TOUCH
            </span>
            <h2>Popular Birthday Add-Ons</h2>

            <p>
              Many guests combine a cake with a bouquet or gift, a fog entry
              for the birthday person and karaoke for the group. Explore the
              available options and build a celebration that suits your plans.
            </p>

            <p className="birthday-decoration-note">
              <strong>A note about decoration:</strong> Every HexaPlay private
              theatre has a fixed, premium decor setup that is pre-installed.
              The decoration cannot be customized, so you simply walk in and
              start celebrating.
            </p>

            <button
              type="button"
              className="birthday-btn birthday-btn-primary"
              onClick={() => openPopup("addons")}
            >
              Explore Add-Ons <span aria-hidden="true">↗</span>
            </button>
          </div>

          <div className="birthday-addons-visual">
            <div className="birthday-addon-orb birthday-addon-orb-one" />
            <div className="birthday-addon-orb birthday-addon-orb-two" />

            <div className="birthday-addon-feature">
              <span className="birthday-addon-feature-icon" aria-hidden="true">
                🎂
              </span>
              <div>
                <h3>Birthday Cake</h3>
                <p>Add a sweet highlight to your celebration.</p>
              </div>
            </div>

            <div className="birthday-addon-feature">
              <span className="birthday-addon-feature-icon" aria-hidden="true">
                💐
              </span>
              <div>
                <h3>Bouquets &amp; Gifts</h3>
                <p>Make your surprise more personal.</p>
              </div>
            </div>

            <div className="birthday-addon-feature">
              <span className="birthday-addon-feature-icon" aria-hidden="true">
                ✨
              </span>
              <div>
                <h3>Fog Entry &amp; Karaoke</h3>
                <p>Create a memorable entrance and celebrate together.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Birthday Gallery */}
      <section className="birthday-section birthday-gallery-section">
        <div className="birthday-container">
          <div className="birthday-section-heading">
            <span className="birthday-eyebrow">
              MOMENTS WORTH REMEMBERING
            </span>
            <h2>Birthday Celebration Moments</h2>
            <p>
              Explore birthday celebration ideas and get inspired for your own
              special day.
            </p>
          </div>

          <div className="birthday-gallery-grid">
            <div className="birthday-gallery-placeholder birthday-gallery-large">
              <span className="birthday-gallery-symbol" aria-hidden="true">
                ✦
              </span>
              <p>Birthday Celebration</p>
              <span>Add a real HexaPlay birthday photo</span>
            </div>

            <div className="birthday-gallery-placeholder">
              <span className="birthday-gallery-symbol" aria-hidden="true">
                🎂
              </span>
              <p>Cake Cutting Moments</p>
              <span>Add a real HexaPlay birthday photo</span>
            </div>

            <div className="birthday-gallery-placeholder">
              <span className="birthday-gallery-symbol" aria-hidden="true">
                💐
              </span>
              <p>Surprises &amp; Memories</p>
              <span>Add a real HexaPlay birthday photo</span>
            </div>
          </div>

          <p className="birthday-gallery-note">
            Replace these placeholders with real HexaPlay birthday celebration
            photos before publishing.
          </p>

          <div className="birthday-gallery-actions">
            <button
              type="button"
              className="birthday-btn birthday-btn-secondary"
              onClick={() => openPopup("album")}
            >
              View Album <span aria-hidden="true">↗</span>
            </button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="birthday-section birthday-faq-section">
        <div className="birthday-container birthday-faq-layout">
          <div className="birthday-faq-intro">
            <span className="birthday-eyebrow">GOOD TO KNOW</span>
            <h2>Frequently Asked Questions</h2>
            <p>
              Find answers to common questions about planning your birthday
              celebration at HexaPlay.
            </p>

            <button
              type="button"
              className="birthday-faq-contact"
              onClick={() => openPopup("plan")}
            >
              Ready to plan your celebration?
              <span aria-hidden="true"> ↗</span>
            </button>
          </div>

          <div className="birthday-faq-list">
            {birthdayFaqs.map((faq, index) => (
              <details className="birthday-faq-item" key={faq.question}>
                <summary>
                  <span className="birthday-faq-index">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span>{faq.question}</span>
                  <span className="birthday-faq-toggle" aria-hidden="true">
                    +
                  </span>
                </summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="birthday-final-section">
        <div className="birthday-container birthday-final-content">
          <span className="birthday-eyebrow">MAKE THIS BIRTHDAY COUNT</span>
          <h2>Your Celebration. Your People. Your Memories.</h2>
          <p>
            Walk into a ready-to-celebrate private theatre, add your favorite
            extras and make your birthday one to remember.
          </p>

          <button
            type="button"
            className="birthday-btn birthday-btn-primary"
            onClick={() => openPopup("plan")}
          >
            Plan Your Birthday <span aria-hidden="true">↗</span>
          </button>
        </div>
      </section>

      {/* Informational Popup */}
      {activePopup && (
        <div
          className="birthday-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closePopup();
          }}
        >
          <section
            className="birthday-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="birthday-modal-title"
            aria-describedby="birthday-modal-description"
          >
            <button
              type="button"
              className="birthday-modal-close"
              onClick={closePopup}
              aria-label="Close popup"
              autoFocus
            >
              ×
            </button>

            <div className="birthday-modal-icon" aria-hidden="true">
              {activePopup.icon}
            </div>

            <span className="birthday-eyebrow">HEXAPLAY EXPERIENCES</span>
            <h2 id="birthday-modal-title">{activePopup.title}</h2>
            <p
              id="birthday-modal-description"
              className="birthday-modal-description"
            >
              {activePopup.description}
            </p>

            <ul className="birthday-modal-points">
              {activePopup.points.map((point) => (
                <li key={point}>
                  <span aria-hidden="true">✓</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>

            <p className="birthday-modal-note">{activePopup.note}</p>

            <button
              type="button"
              className="birthday-btn birthday-btn-primary birthday-modal-done"
              onClick={closePopup}
            >
              Got It
            </button>
          </section>
        </div>
      )}
    </main>
  );
};

export default BirthdayCelebration;
